export type KeyColor = 'yellow' | 'cyan' | 'red';

export type LevelDefinition = {
  id: string;
  name: string;
  subtitle: string;
  objective: string;
  targetMinutes: readonly [number, number];
  ambushSize: number;
  grid: string[];
  wallHue: number;
  wallAccent: number;
  tileHue: number;
  floorHue: number;
  lightColors: readonly [number, number, number];
  puddleColor: number;
  fogColor: number;
};

type FacilityTools = {
  set: (row: number, col: number, cell: string) => void;
  place: (cell: string, positions: ReadonlyArray<readonly [number, number]>) => void;
  horizontalWall: (row: number, fromCol: number, toCol: number, gaps?: readonly number[]) => void;
  verticalWall: (col: number, fromRow: number, toRow: number, gaps?: readonly number[]) => void;
};

const makeGrid = (rows: string[]): string[] => {
  const width = rows[0]?.length ?? 0;
  if (!width || rows.some((row) => row.length !== width)) {
    throw new Error('La mappa contiene righe di lunghezza diversa.');
  }
  return rows;
};

const makeFacility = (width: number, height: number, configure: (tools: FacilityTools) => void): string[] => {
  const cells: string[][] = Array.from({ length: height }, (_, row) =>
    Array.from({ length: width }, (_, col) => row === 0 || row === height - 1 || col === 0 || col === width - 1 ? '#' : '.'),
  );
  const set = (row: number, col: number, cell: string): void => {
    if (row <= 0 || row >= height - 1 || col <= 0 || col >= width - 1) {
      throw new Error(`Elemento fuori dalla struttura: ${row},${col}`);
    }
    cells[row][col] = cell;
  };
  const place = (cell: string, positions: ReadonlyArray<readonly [number, number]>): void => {
    for (const [row, col] of positions) set(row, col, cell);
  };
  const horizontalWall = (row: number, fromCol: number, toCol: number, gaps: readonly number[] = []): void => {
    for (let col = fromCol; col <= toCol; col += 1) if (!gaps.includes(col)) set(row, col, '#');
  };
  const verticalWall = (col: number, fromRow: number, toRow: number, gaps: readonly number[] = []): void => {
    for (let row = fromRow; row <= toRow; row += 1) if (!gaps.includes(row)) set(row, col, '#');
  };
  configure({ set, place, horizontalWall, verticalWall });
  return makeGrid(cells.map((row) => row.join('')));
};

const depuratore = makeFacility(45, 27, ({ set, place, horizontalWall, verticalWall }) => {
  verticalWall(15, 1, 25);
  verticalWall(30, 1, 25);
  horizontalWall(6, 1, 14, [12]);
  horizontalWall(12, 1, 14, [3]);
  horizontalWall(18, 1, 14, [12]);
  horizontalWall(10, 16, 29, [28]);
  horizontalWall(16, 16, 29, [17]);
  horizontalWall(22, 16, 29, [28]);
  horizontalWall(15, 31, 43, [42]);
  horizontalWall(9, 31, 43, [32]);
  horizontalWall(4, 4, 10, [7]);
  verticalWall(7, 13, 17, [15]);
  horizontalWall(20, 18, 25, [21, 24]);
  verticalWall(35, 10, 14, [12]);
  horizontalWall(24, 34, 41, [37]);
  set(7, 15, 'Y');
  set(19, 30, 'C');
  set(2, 2, 'S');
  set(23, 3, 'y');
  set(24, 17, 'c');
  set(3, 42, 'X');
  place('d', [[4, 10], [9, 5], [14, 12], [20, 5], [23, 11], [8, 22], [13, 27], [18, 19], [23, 25], [21, 38], [17, 33], [12, 40], [7, 35], [3, 38]]);
  place('q', [[10, 10], [20, 26], [18, 42], [5, 33]]);
  place('H', [[14, 5], [18, 24], [11, 35]]);
  place('A', [[5, 5], [11, 20], [17, 27], [21, 34], [6, 40]]);
  place('L', [[5, 13], [3, 31]]);
  place('T', [[11, 9], [15, 21], [10, 38]]);
  place('?', [[14, 15], [18, 8]]);
});

const bagnoIndustriale = makeFacility(49, 29, ({ set, place, horizontalWall, verticalWall }) => {
  verticalWall(12, 1, 27);
  verticalWall(24, 1, 27);
  verticalWall(36, 1, 27);
  horizontalWall(7, 1, 11, [10]);
  horizontalWall(14, 1, 11, [2]);
  horizontalWall(21, 1, 11, [10]);
  horizontalWall(12, 13, 23, [22]);
  horizontalWall(18, 13, 23, [14]);
  horizontalWall(24, 13, 23, [22]);
  horizontalWall(17, 25, 35, [34]);
  horizontalWall(11, 25, 35, [26]);
  horizontalWall(10, 37, 47, [47]);
  horizontalWall(16, 37, 47, [38]);
  horizontalWall(22, 37, 47, [47]);
  verticalWall(6, 3, 6, [5]);
  horizontalWall(16, 3, 9, [6]);
  verticalWall(18, 3, 9, [6]);
  horizontalWall(21, 27, 33, [30]);
  verticalWall(43, 11, 15, [13]);
  horizontalWall(25, 39, 45, [42]);
  set(8, 12, 'C');
  set(21, 24, 'R');
  set(6, 36, 'Y');
  set(2, 2, 'S');
  set(26, 2, 'c');
  set(26, 14, 'r');
  set(4, 34, 'y');
  set(26, 38, 'X');
  place('d', [
    [4, 8], [9, 4], [12, 10], [17, 4], [23, 8], [26, 9],
    [8, 18], [14, 21], [16, 15], [21, 19], [26, 20], [5, 14],
    [23, 29], [19, 34], [14, 28], [8, 32], [4, 27], [4, 32],
    [8, 42], [13, 46], [19, 40], [24, 46], [26, 43], [14, 39],
  ]);
  place('q', [[11, 7], [23, 3], [10, 16], [20, 22], [14, 33], [7, 27], [12, 44], [24, 40]]);
  place('H', [[16, 8], [20, 16], [8, 28], [19, 45]]);
  place('A', [[5, 5], [10, 20], [16, 32], [20, 44], [25, 16], [6, 40]]);
  place('L', [[5, 10], [25, 46]]);
  place('T', [[11, 5], [15, 29], [20, 41], [26, 27]]);
  place('?', [[9, 24], [17, 12], [16, 43]]);
});

const santuarioVasca = makeFacility(53, 31, ({ set, place, horizontalWall, verticalWall }) => {
  verticalWall(13, 1, 29);
  verticalWall(26, 1, 29);
  verticalWall(39, 1, 29);
  horizontalWall(7, 1, 12, [11]);
  horizontalWall(14, 1, 12, [2]);
  horizontalWall(21, 1, 12, [11]);
  horizontalWall(24, 14, 25, [15]);
  horizontalWall(17, 14, 25, [24]);
  horizontalWall(10, 14, 25, [15]);
  horizontalWall(8, 27, 38, [37]);
  horizontalWall(15, 27, 38, [28]);
  horizontalWall(22, 27, 38, [37]);
  horizontalWall(24, 40, 51, [50]);
  horizontalWall(17, 40, 51, [41]);
  horizontalWall(10, 40, 51, [50]);
  horizontalWall(5, 40, 51, [41]);
  horizontalWall(3, 40, 51, [50]);
  horizontalWall(4, 4, 10, [7]);
  verticalWall(7, 16, 20, [18]);
  horizontalWall(26, 16, 23, [19, 22]);
  verticalWall(32, 9, 13, [11]);
  horizontalWall(19, 29, 36, [32]);
  verticalWall(46, 18, 22, [20]);
  set(23, 13, 'Y');
  set(7, 26, 'C');
  set(26, 39, 'R');
  set(2, 2, 'S');
  set(27, 2, 'y');
  set(3, 24, 'c');
  set(27, 37, 'r');
  set(2, 41, 'X');
  set(7, 45, 'B');
  place('d', [
    [4, 9], [10, 4], [12, 11], [17, 5], [23, 8], [27, 10],
    [27, 20], [21, 16], [19, 23], [14, 18], [7, 23], [4, 16], [3, 21],
    [5, 34], [11, 30], [13, 37], [18, 31], [21, 36], [25, 30], [27, 34],
    [27, 46], [21, 42], [19, 50], [14, 45], [12, 50], [7, 42], [4, 42],
  ]);
  place('q', [[11, 9], [24, 4], [20, 20], [13, 15], [6, 22], [12, 34], [24, 34], [21, 48], [13, 42], [6, 48]]);
  place('H', [[16, 10], [22, 18], [12, 29], [26, 43], [8, 45]]);
  place('A', [[5, 5], [12, 20], [19, 34], [26, 47], [6, 47], [27, 16]]);
  place('L', [[6, 11], [28, 49]]);
  place('T', [[12, 8], [20, 18], [11, 35], [25, 45], [6, 44]]);
  place('?', [[16, 13], [18, 26], [20, 46]]);
});

const cameraBolle = makeFacility(35, 23, ({ place, horizontalWall, verticalWall }) => {
  horizontalWall(6, 5, 29, [9, 17, 25]);
  horizontalWall(16, 5, 29, [9, 17, 25]);
  verticalWall(9, 7, 15, [11]);
  verticalWall(25, 7, 15, [11]);
  horizontalWall(11, 12, 22, [17]);
  place('S', [[11, 17]]);
  place('A', [[3, 3], [3, 31], [19, 3], [19, 31]]);
  place('H', [[3, 17], [19, 17]]);
  place('T', [[8, 14], [8, 20], [14, 14], [14, 20]]);
  place('L', [[11, 4], [11, 30]]);
});

export const LEVELS: LevelDefinition[] = [
  {
    id: 'depuratore',
    name: 'Il Depuratore',
    subtitle: 'La rete di scarico nasconde condotti, montacarichi e valvole abusive.',
    objective: 'Recupera le chiavi gialla e ciano, poi bonifica lo scarico',
    targetMinutes: [7, 10],
    ambushSize: 6,
    wallHue: 0x3d4647,
    wallAccent: 0x176875,
    tileHue: 0xc15d28,
    floorHue: 0x202d30,
    lightColors: [0x35c9dc, 0xff8a32, 0x77e2a8],
    puddleColor: 0x35cde0,
    fogColor: 0x111819,
    grid: depuratore,
  },
  {
    id: 'bagno-industriale',
    name: 'Bagno Industriale',
    subtitle: 'Lavanderia automatica, vapori roventi e troppe paperelle.',
    objective: 'Apri le paratie ciano, rossa e gialla e ripulisci i settori',
    targetMinutes: [9, 12],
    ambushSize: 7,
    wallHue: 0x4a4440,
    wallAccent: 0x8c3f2b,
    tileHue: 0x2b8d89,
    floorHue: 0x302822,
    lightColors: [0xff7940, 0x58e0d6, 0xe4c441],
    puddleColor: 0x42d1c6,
    fogColor: 0x181311,
    grid: bagnoIndustriale,
  },
  {
    id: 'santuario-vasca',
    name: 'Santuario della Vasca',
    subtitle: 'Il Quackodemon attende oltre la vasca viola.',
    objective: 'Sblocca i tre settori e bonifica il santuario del Quackodemon',
    targetMinutes: [10, 14],
    ambushSize: 8,
    wallHue: 0x40384a,
    wallAccent: 0x693e88,
    tileHue: 0x147f98,
    floorHue: 0x27202f,
    lightColors: [0xb260e8, 0x32d0e0, 0xff743d],
    puddleColor: 0x895fd9,
    fogColor: 0x130f18,
    grid: santuarioVasca,
  },
];

export const HORDE_LEVEL: LevelDefinition = {
  id: 'camera-bolle',
  name: 'Camera delle Bolle',
  subtitle: 'Le valvole non smetteranno di sputare paperelle.',
  objective: 'Resisti alle ondate e difendi la pompa centrale',
  targetMinutes: [0, 0],
  ambushSize: 0,
  wallHue: 0x34464c,
  wallAccent: 0x9b4228,
  tileHue: 0x7b4ca8,
  floorHue: 0x1c292d,
  lightColors: [0x58c9d4, 0xe46628, 0x895fd9],
  puddleColor: 0x4ac9c7,
  fogColor: 0x0e171a,
  grid: cameraBolle,
};

export const KEY_BY_CELL: Record<string, KeyColor> = {
  y: 'yellow',
  c: 'cyan',
  r: 'red',
};

export const DOOR_BY_CELL: Record<string, KeyColor> = {
  Y: 'yellow',
  C: 'cyan',
  R: 'red',
};

export const isWallCell = (cell: string): boolean => cell === '#';

export const isSecretCell = (cell: string): boolean => cell === '?';
export const isElevatorCell = (cell: string): boolean => cell === 'L';
export const isTrapCell = (cell: string): boolean => cell === 'T';

const spreadCells = (
  candidates: Array<{ row: number; col: number }>,
  count: number,
  seed: number,
): Array<{ row: number; col: number }> => {
  if (candidates.length <= count) return candidates;
  const pool = [...candidates];
  const selected = [pool.splice(Math.abs(seed) % pool.length, 1)[0]];
  while (selected.length < count && pool.length > 0) {
    let bestIndex = 0;
    let bestScore = -1;
    for (let index = 0; index < pool.length; index += 1) {
      const candidate = pool[index];
      const minimumDistance = Math.min(...selected.map((cell) => {
        const rowDistance = candidate.row - cell.row;
        const colDistance = candidate.col - cell.col;
        return rowDistance * rowDistance + colDistance * colDistance;
      }));
      const tieBreaker = ((candidate.row * 73856093) ^ (candidate.col * 19349663) ^ seed) & 0xff;
      const score = minimumDistance * 512 + tieBreaker;
      if (score > bestScore) {
        bestScore = score;
        bestIndex = index;
      }
    }
    selected.push(pool.splice(bestIndex, 1)[0]);
  }
  return selected;
};

export const getKeyAmbushCells = (
  grid: string[],
  startRow: number,
  startCol: number,
  count: number,
): Array<{ row: number; col: number }> => {
  const height = grid.length;
  const width = grid[0]?.length ?? 0;
  const queue: Array<{ row: number; col: number; distance: number }> = [
    { row: startRow, col: startCol, distance: 0 },
  ];
  const visited = new Set<string>();
  const candidates: Array<{ row: number; col: number }> = [];
  const directions = [
    [-1, 0],
    [0, 1],
    [1, 0],
    [0, -1],
  ] as const;

  while (queue.length > 0) {
    const current = queue.shift()!;
    const cellId = `${current.row}:${current.col}`;
    if (visited.has(cellId)) continue;
    visited.add(cellId);

    if (current.distance >= 2) candidates.push({ row: current.row, col: current.col });
    if (current.distance >= 12) continue;

    for (const [rowDelta, colDelta] of directions) {
      const row = current.row + rowDelta;
      const col = current.col + colDelta;
      if (row < 0 || row >= height || col < 0 || col >= width) continue;
      const cell = grid[row][col];
      if (cell === '#' || DOOR_BY_CELL[cell]) continue;
      queue.push({ row, col, distance: current.distance + 1 });
    }
  }

  return spreadCells(candidates, count, startRow * 97 + startCol * 193);
};

export const getReinforcementCells = (
  grid: string[],
  count: number,
  seed: number,
): Array<{ row: number; col: number }> => {
  const candidates: Array<{ row: number; col: number }> = [];
  let start = { row: 0, col: 0 };
  for (let row = 0; row < grid.length; row += 1) {
    for (let col = 0; col < grid[row].length; col += 1) {
      if (grid[row][col] === 'S') start = { row, col };
    }
  }
  for (let row = 1; row < grid.length - 1; row += 1) {
    for (let col = 1; col < grid[row].length - 1; col += 1) {
      if (grid[row][col] !== '.') continue;
      if (Math.abs(row - start.row) + Math.abs(col - start.col) < 6) continue;
      candidates.push({ row, col });
    }
  }
  return spreadCells(candidates, count, seed);
};
